import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import {
    ITrainingListItemFragment,
    TrainingListItemFragmentDoc,
} from 'graphql/types';
import { TrainingListItem } from 'training/components/TrainingListItem';
import {
    getTrainingSelectStatus,
    isTrainingAdded,
    isTrainingSelected,
} from 'training/utils/trainingSelect';
import { AddButton } from 'common/components/Button/AddButton';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { TrainingSelectDrawer } from 'training/components/TrainingSelectDrawer';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    isEditable?: boolean;
    label?: string;
    name: string;
    noResultsLabel?: string;
}

export const TrainingSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    isEditable = true,
    label,
    name,
    noResultsLabel,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedTrainings, setSelectedTrainings] = useState<
        ITrainingListItemFragment[]
    >([]);
    const navigate = useNavigate();
    const location = useLocation();
    const client = useApolloClient();

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedTrainingIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedTrainings = useMemo(() => {
        // Get training objects from cache based on the training ids set in the form
        const trainings =
            addedTrainingIds
                ?.map((addedTrainingId) => {
                    try {
                        return client.cache.readFragment({
                            id: `TrainingListItem:${addedTrainingId}`,
                            fragment: TrainingListItemFragmentDoc,
                            fragmentName: 'TrainingListItem',
                        });
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return trainings;
    }, [addedTrainingIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedTrainings([]);

        setDrawerOpen(open);
    };

    const handleSelectTraining = (training: ITrainingListItemFragment) => {
        const newTrainings = [...selectedTrainings, training].sort((a, b) =>
            a.title > b.title ? 1 : -1
        );

        setSelectedTrainings(newTrainings);
    };

    const handleRemoveTraining = (training: ITrainingListItemFragment) => {
        const { isSelected } = getTrainingSelectStatus(
            selectedTrainings,
            addedTrainings,
            training
        );

        if (isSelected) {
            const newSelectedTrainings = selectedTrainings.filter(
                (selectedTraining) => selectedTraining.id !== training.id
            );

            setSelectedTrainings(newSelectedTrainings);

            return;
        }

        setValue(
            name,
            addedTrainings
                .filter((addedTraining) => addedTraining.id !== training.id)
                .map((training) => training.id),
            { shouldDirty: true }
        );
    };

    const handleAddTrainings = () => {
        setValue(
            name,
            [
                ...(addedTrainingIds || []),
                ...selectedTrainings.map(
                    (selectedTraining) => selectedTraining.id
                ),
            ],
            { shouldDirty: true }
        );

        setSelectedTrainings([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {isEditable && (
                    <AddButton
                        aria-label={translate('addTrainingsTitle')}
                        error={
                            !!error?.message && isTouched
                                ? error.message
                                : undefined
                        }
                        onClick={() => handleDrawerOpen(true)}
                    >
                        {label || translate('addTrainingsTitle')}
                    </AddButton>
                )}

                {!!addedTrainings.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: isEditable ? 2 : 0,
                        }}
                    >
                        {addedTrainings.map((addedTraining) => (
                            <TrainingListItem
                                addButton={isEditable}
                                added={isTrainingAdded(
                                    addedTrainings,
                                    addedTraining
                                )}
                                key={`added-${addedTraining.id}`}
                                selected={isTrainingSelected(
                                    selectedTrainings,
                                    addedTraining
                                )}
                                training={addedTraining}
                                onClick={(
                                    training: ITrainingListItemFragment
                                ) => {
                                    if (!isEditable) {
                                        gotoTrainingPage(
                                            navigate,
                                            training.id,
                                            location
                                        );

                                        return;
                                    }

                                    handleRemoveTraining(training);
                                }}
                            />
                        ))}
                    </Box>
                )}

                {!isEditable && !addedTrainings.length && (
                    <Box>
                        <Typography>
                            {noResultsLabel ||
                                translate('noOptionsText.trainings')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <TrainingSelectDrawer
                addedTrainings={addedTrainings}
                open={drawerOpen}
                selectedTrainings={selectedTrainings}
                onAddTrainings={handleAddTrainings}
                onDrawerOpen={handleDrawerOpen}
                onRemoveTraining={handleRemoveTraining}
                onSelectTraining={handleSelectTraining}
            />
        </Box>
    );
};
